import { useState, useEffect } from "react";
import { ButtonPopup, ButtonSiguiente, Select } from "../../ui";
import { safeFetch } from "@/api/safeFetch";
import { useNavigate } from "react-router-dom";

interface Seccion {
    id: number;
    nombreSeccion: string;
}

const BorrarSecciones: React.FC = () => {
    const [secciones, setSecciones] = useState<Seccion[]>([]);
    const [selectedSeccionId, setSelectedSeccionId] = useState<number | null>(null);
    const [selectedNombre, setSelectedNombre] = useState<string | null>(null);
    const [showAlert, setShowAlert] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const navigate = useNavigate();

    // Cargar secciones
    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const fetchSecciones = async () => {
            try {
                const res = await safeFetch("/api/seccion", { signal });
                if (!res.ok) throw new Error("Error cargando secciones");
                const data = await res.json();
                setSecciones(data);
            } catch (err: any) {
                if (err.name !== "AbortError") {
                    setError(err.message || "Error al cargar secciones");
                }
            }
        };

        fetchSecciones();

        return () => controller.abort();
    }, []);


    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = parseInt(e.target.value);
        setSelectedSeccionId(id);
        const seccion = secciones.find((s) => s.id === id);
        setSelectedNombre(seccion?.nombreSeccion || null);
    };

    const token = localStorage.getItem("token");

    const handleDelete = async () => {
        if (!selectedSeccionId) return;
        setLoading(true);
        try {
            const res = await safeFetch(`/api/backoffice/secciones/${selectedSeccionId}`, {

                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
            );
            if (!res.ok) throw new Error("Error al borrar la sección");

            setSuccess("Sección borrada correctamente 🎉");
            setSelectedSeccionId(null);
            setSelectedNombre(null);
            setSecciones(secciones.filter((s) => s.id !== selectedSeccionId));
            setShowAlert(false);
        } catch (err: any) {
            setError(err.message || "Ocurrió un error.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            setSecciones([]);
            setSelectedSeccionId(null);
            setSelectedNombre(null);
            setShowAlert(false);
            setLoading(false);
            setError("");
            setSuccess("");
        };
    }, []);


    return (
        <div className="w-full h-screen flex items-center justify-center relative bg-zinc-500 p-4">

            {/* 🔙 Botón volver arriba izquierda */}
            <div className="absolute top-4 left-4">
                <ButtonSiguiente
                    onClick={() => navigate("/backoffice")}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
                >
                    ⬅ Volver
                </ButtonSiguiente>
            </div>

            {/* Contenedor principal */}
            <div className="w-full max-w-md p-6 bg-zinc-400 shadow-md shadow-green-700 rounded-xl flex flex-col items-center gap-6 border-zinc-600 border-r-4 border-t-2">

                <h2 className="text-2xl font-bold text-center mb-2">Borrar Secciones</h2>

                {error && <p className="text-red-600">{error}</p>}
                {success && <p className="text-green-600">{success}</p>}

                {/* Columna de tres bloques: Select, Nombre, Botón */}
                <div className="w-full flex flex-col items-center gap-4">

                    {/* 1️⃣ Select */}
                    <div className="w-full">
                        <label className="font-medium block mb-1">Selecciona la Sección</label>
                        <Select
                            value={selectedSeccionId ?? ""}
                            onChange={handleSelectChange}
                            options={secciones.map(s => ({ value: s.id, label: s.nombreSeccion }))}
                            placeholder="-- Selecciona --"
                            className="dark:bg-white"
                        />
                    </div>

                    {/* 2️⃣ Nombre seleccionado */}
                    {selectedNombre && (
                        <p className="font-semibold text-center text-lg">{selectedNombre}</p>
                    )}

                    {/* 3️⃣ Botón Borrar */}
                    <ButtonSiguiente
                        type="button"
                        disabled={!selectedSeccionId || loading}
                        className="bg-red-600 text-zinc-700 py-2 rounded hover:bg-red-700 transition w-full"
                        onClick={() => setShowAlert(true)}
                    >
                        {loading ? "Eliminando..." : "Borrar Sección"}
                    </ButtonSiguiente>

                </div>
            </div>

            {/* Modal de confirmación */}
            {showAlert && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
                    <div
                        className="relative w-80 p-6 text-center bg-white/50 backdrop-blur-0 shadow-lg text-zinc-900 border rounded-[1.5rem] overflow-hidden border-blue-700"

                    >
                        <p className="mb-4">¿Estás seguro de que deseas borrar esta sección?</p>
                        <div className="flex justify-between gap-4">
                            <ButtonPopup
                                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition"
                                onClick={() => setShowAlert(false)}
                            >
                                Volver
                            </ButtonPopup>
                            <ButtonPopup
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                                onClick={handleDelete}
                            >
                                Aceptar
                            </ButtonPopup>
                        </div>
                    </div>
                </div>

            )}

        </div>
    );
};

export default BorrarSecciones;
